import type { ReactNode } from 'react';
import type { RevealGroup, RevealRead } from '../../hooks/useRevealGroup';
import { useSkeletonAppearance } from '../../hooks/useSkeletonAppearance';
import { cn } from '../../lib/utils';
import { ReadError } from './ReadError';

/** A read that can say it failed and be retried: any TanStack query result. */
export interface RetryableRead extends RevealRead {
  isError: boolean;
  refetch: () => unknown;
}

interface RevealBaseProps {
  group: RevealGroup;
  /** Rendered until the group is revealed and these reads have settled. Omit for a slot that holds no space while loading. */
  placeholder?: ReactNode;
  /** `span` inside a line of text (a meta row, a figure), else `div`. */
  as?: 'div' | 'span';
  className?: string;
  children: ReactNode;
}

type RevealProps = RevealBaseProps & (
  | {
      read: RevealRead | readonly RevealRead[];
      errorMessage?: never;
    }
  | {
      read: RetryableRead;
      /** Shown with a Retry, in place of the content, when the read fails: "Couldn't load WIP". */
      errorMessage: string;
    }
);

/**
 * The one place a loading region decides what to show: the placeholder, the
 * failed-read message, or the content with its arrival fade. Callers declare
 * what they wait on and what fills the space; the timing lives in
 * `useRevealGroup`, the presentation here, so changing either never touches a
 * call site. Pattern: `apps/web/bill/src/components/ui/CLAUDE.md` § "Loading".
 */
export function Reveal({ group, read, placeholder = null, errorMessage, as: Tag = 'div', className, children }: RevealProps) {
  const reads: readonly RevealRead[] = Array.isArray(read) ? read : [read];
  if (!reads.every(group.ready)) return placeholder === null ? null : <Placeholder as={Tag}>{placeholder}</Placeholder>;
  if (errorMessage !== undefined && !Array.isArray(read) && 'refetch' in read && read.isError) {
    const retryable = read;
    return <ReadError message={errorMessage} onRetry={() => void retryable.refetch()} className={className} />;
  }
  // An empty slot renders nothing, so a separator-drawing parent (MetaRow) gains no stray child.
  if (children === null || children === undefined || children === false) return null;
  return <Tag className={cn(group.revealClass, className) || undefined}>{children}</Tag>;
}

/**
 * Gives every placeholder the shared appearance (invisible for a moment, then a
 * fade, continuing at once when it replaces a visible one), whatever it holds:
 * a `SkeletonRegion`, or a bare `Skeleton` bar in a line of text.
 */
function Placeholder({ as: Tag, children }: { as: 'div' | 'span'; children: ReactNode }) {
  const appearance = useSkeletonAppearance<HTMLDivElement & HTMLSpanElement>();
  return <Tag ref={appearance.ref} className={appearance.className} style={appearance.style}>{children}</Tag>;
}
